import React, { useCallback, useEffect, useState } from "react";
import Layout from "../components/layout";
import Deco from "../components/Deco";

const STORAGE_KEY = "women_in_wine_proposal_v2_unlocked";

const PDF_PATH = `${process.env.PUBLIC_URL || ""}/client-proposals/women-in-wine-label-proposals-v2.pdf`;

export default function WomenInWineProposalPage() {
  const configuredPassword =
    (process.env.REACT_APP_WOMEN_IN_WINE_PROPOSAL_PASSWORD || "").trim();
  const [unlocked, setUnlocked] = useState(false);
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    try {
      if (sessionStorage.getItem(STORAGE_KEY) === "1") {
        setUnlocked(true);
      }
    } catch (_e) {
      // sessionStorage unavailable
    }
  }, []);

  const handleUnlock = useCallback(
    (e) => {
      e.preventDefault();
      setError("");

      if (!configuredPassword) {
        setError(
          "Client access is not configured for this site build. Please contact Casey if you need help."
        );
        return;
      }

      if (password.trim() !== configuredPassword) {
        setError("That access code does not match. Please try again or reach out if you need a new code.");
        return;
      }

      try {
        sessionStorage.setItem(STORAGE_KEY, "1");
      } catch (_err) {
        // still allow viewing this session
      }
      setUnlocked(true);
      setPassword("");
    },
    [configuredPassword, password]
  );

  return (
    <Layout>
      <div className="deco-wrap">
        <Deco name="one" className="deco-contact deco-contact--left" />
        <Deco name="two" className="deco-contact deco-contact--right" />
      </div>
      <div className="client-proposal">
        <div className="client-proposal__inner">
          <p className="client-proposal__eyebrow">Client proposal</p>
          <h1 className="client-proposal__title">Women in Wine NJ — label options</h1>

          {!unlocked ? (
            <>
              <p className="client-proposal__intro">
                This page is private. Enter the access code you were sent to view
                the label design proposal PDF and a quick on-page summary of both
                options.
              </p>
              <form className="client-proposal__lock" onSubmit={handleUnlock}>
                <div className="client-proposal__field">
                  <label className="client-proposal__label" htmlFor="proposal-access">
                    Access code
                  </label>
                  <input
                    id="proposal-access"
                    className="client-proposal__input"
                    type="password"
                    autoComplete="current-password"
                    value={password}
                    onChange={(ev) => setPassword(ev.target.value)}
                    placeholder="Enter your code"
                  />
                </div>
                <div className="client-proposal__actions">
                  <button type="submit" className="a-button a-button--primary">
                    View proposal
                  </button>
                </div>
                {error ? (
                  <p className="client-proposal__status client-proposal__status--error" role="alert">
                    {error}
                  </p>
                ) : null}
                <p className="client-proposal__lock-note">
                  Having trouble? Email{" "}
                  <a href="mailto:casey@alvarezbranding.com">casey@alvarezbranding.com</a>
                  .
                </p>
              </form>
            </>
          ) : (
            <>
              <p className="client-proposal__intro">
                Below is a succinct overview of both proposal options. The full
                multi-page brief is in the PDF viewer — you can also download it
                to share internally or print.
              </p>

              <h2 className="client-proposal__section-title">Overview</h2>
              <p className="client-proposal__overview">
                Both options include multiple design directions. The difference is
                the level of refinement, exploration, and collaboration through
                the process.
              </p>

              <h2 className="client-proposal__section-title">Options</h2>
              <div className="client-proposal__options">
                <article className="client-proposal-option">
                  <h3 className="client-proposal-option__heading">
                    Option 1 — Full label design
                  </h3>
                  <p className="client-proposal-option__price">$2,200</p>
                  <p className="client-proposal-option__sub">Scope of work</p>
                  <ul className="client-proposal-option__list">
                    <li>2–3 initial design directions (fully developed concepts)</li>
                    <li>Selection and refinement of one direction</li>
                    <li>2–3 rounds of revisions</li>
                    <li>Detailed typography and layout refinement</li>
                    <li>Label system for white, red, and rosé</li>
                    <li>Print-ready and web-ready deliverables</li>
                  </ul>
                  <div className="client-proposal-option__meta">
                    <p>
                      <strong>Timeline:</strong> 3–4 weeks
                    </p>
                    <p>
                      <strong>Ideal for:</strong> A more collaborative process with room
                      to explore and refine
                    </p>
                  </div>
                </article>

                <article className="client-proposal-option">
                  <h3 className="client-proposal-option__heading">
                    Option 2 — Streamlined label design
                  </h3>
                  <p className="client-proposal-option__price">$1,500</p>
                  <p className="client-proposal-option__sub">Scope of work</p>
                  <ul className="client-proposal-option__list">
                    <li>2 initial design directions (focused concepts)</li>
                    <li>Selection of one direction for refinement</li>
                    <li>1–2 rounds of revisions</li>
                    <li>Simplified adaptation across wine variants</li>
                    <li>Print-ready and web-ready deliverables</li>
                  </ul>
                  <div className="client-proposal-option__meta">
                    <p>
                      <strong>Timeline:</strong> 2–3 weeks
                    </p>
                    <p>
                      <strong>Ideal for:</strong> A faster, more efficient process with a
                      defined direction
                    </p>
                  </div>
                </article>
              </div>

              <section className="client-proposal__pdf" aria-label="Proposal PDF">
                <h2 className="client-proposal__pdf-title">Full proposal document</h2>
                <p className="client-proposal__pdf-lead">
                  On phones, downloading or opening in a new tab often works best. You can
                  still try the embedded preview below.
                </p>
                <div className="client-proposal__pdf-links">
                  <a
                    className="a-button a-button--primary"
                    href={PDF_PATH}
                    download="Women-in-Wine-NJ-Label-Proposals-v2.pdf"
                  >
                    Download PDF
                  </a>
                  <a
                    className="a-button a-button--secondary"
                    href={PDF_PATH}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Open in new tab
                  </a>
                </div>
                <div className="client-proposal__pdf-frame-wrap">
                  <iframe
                    title="Women in Wine NJ label proposal PDF"
                    className="client-proposal__pdf-frame"
                    src={PDF_PATH}
                  />
                </div>
                <p className="client-proposal__pdf-fallback">
                  If the preview does not load, use{" "}
                  <a href={PDF_PATH} download>
                    download
                  </a>{" "}
                  or{" "}
                  <a href={PDF_PATH} target="_blank" rel="noopener noreferrer">
                    open in a new tab
                  </a>
                  .
                </p>
              </section>
            </>
          )}
        </div>
      </div>
    </Layout>
  );
}
